import { Text, Avatar, Box, Flex, HStack, IconButton, VStack, Badge, Tooltip, Icon } from '@chakra-ui/react'
import { useMyProfile } from '../../api/api_hooks/user/myProfile'
import type { FC } from 'react'
import { chakra } from '@chakra-ui/react'
import { IoBusinessSharp as RawBuildingIcon } from 'react-icons/io5'
import NotificationsPanel from './NotificationsPanel'
import { LuCalendarDays } from 'react-icons/lu'
import { useTranslation } from 'react-i18next'
import { useLabAppAccess } from '../../hooks/useLabAppAccess'
import { LAB_APPS, labAppPath } from '../../config/labApps'
import { useNavigate } from 'react-router-dom'
import { PlanType } from '../../types/subscription'
import { CHROME_HEADER_GRADIENT, CHROME_AURORA_VIOLET, CHROME_AURORA_CYAN } from '../../theme/chrome'
import { useAuthedImage } from '../../hooks/useAuthedImage'

const BuildingIcon = chakra(RawBuildingIcon as FC)

function getGreetingKey(): string {
  const h = new Date().getHours()
  if (h < 12) return 'home.greeting.morning'
  if (h < 18) return 'home.greeting.afternoon'
  return 'home.greeting.evening'
}

function getFormattedDate(): string {
  return new Date().toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export default function HomeHeader() {
  const { data: userData } = useMyProfile()
  const avatarSrc = useAuthedImage(userData?.avatarFilePath)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isAppEnabled } = useLabAppAccess()

  const isPremium = userData?.plan === PlanType.PREMIUM
  const isEnterprise = userData?.plan === PlanType.ENTERPRISE

  // Les actions rapides viennent du registre du Lab Studio : une application
  // ajoutée là apparaît ici d'elle-même, et une application coupée dans
  // l'administration ne se lance pas depuis l'accueil.
  const quickActions = LAB_APPS
    .filter((app) => isAppEnabled(app.id))
    .map((app) => ({
      icon: app.icon,
      label: t('home.quickActions.newAnalysis', { app: app.label }),
      path: labAppPath(app.id),
      color: app.accentColor,
    }))

  return (
    <Box
      data-tour-id="home-hero"
      bgGradient={CHROME_HEADER_GRADIENT}
      borderRadius="2xl"
      p={{ base: 5, md: 6 }}
      position="relative"
      overflow="hidden"
      boxShadow="0 20px 60px rgba(15, 10, 26, 0.45)"
      // Glass edge — a thin violet→cyan gradient border traced with a mask, same
      // language as the neon-glass sidebar.
      sx={{
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: '1px',
          background: 'linear-gradient(135deg, rgba(167,139,250,0.65), rgba(34,211,238,0.45) 45%, transparent 75%)',
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          pointerEvents: 'none',
        },
      }}
    >
      {/* Aurora blobs — duo-tone violet + cyan (no more pink, which read as ALMA) */}
      <Box position="absolute" top="-60px" right="-40px" w="240px" h="240px" borderRadius="full"
        bg={CHROME_AURORA_VIOLET} pointerEvents="none" />
      <Box position="absolute" bottom="-90px" left="28%" w="320px" h="320px" borderRadius="full"
        bg={CHROME_AURORA_CYAN} pointerEvents="none" />
      <Box position="absolute" top="-40px" left="-30px" w="200px" h="200px" borderRadius="full"
        bg="radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)" pointerEvents="none" />

      {/* Floating date chip — top-right, separate from action buttons so the
          left greeting stays uncluttered and the date reads as a status pill. */}
      <HStack
        display={{ base: 'none', md: 'inline-flex' }}
        position="absolute"
        top={4}
        right={5}
        spacing={2}
        px={3}
        py={1.5}
        borderRadius="full"
        bg="rgba(255,255,255,0.10)"
        border="1px solid"
        borderColor="whiteAlpha.300"
        backdropFilter="blur(10px)"
        boxShadow="0 4px 14px -4px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.12)"
        zIndex={2}
        sx={{
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            padding: '1px',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.45), rgba(255,255,255,0) 60%)',
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
          },
        }}
      >
        <Icon as={LuCalendarDays} boxSize={3.5} color="whiteAlpha.800" />
        <Text fontSize="xs" color="white" fontWeight="600" letterSpacing="0.01em" textTransform="capitalize">
          {getFormattedDate()}
        </Text>
      </HStack>

      <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} position="relative" zIndex={1}
        flexDirection={{ base: 'column', md: 'row' }} gap={4} mt={{ base: 0, md: 6 }}>

          {/* Left — avatar + greeting */}
          <HStack spacing={4} align="center">
            <Box
              position="relative"
              borderRadius="full"
              p="2px"
              bgGradient="linear(135deg, #a78bfa, #22d3ee)"
              boxShadow="0 0 18px rgba(167,139,250,0.45), 0 0 30px rgba(34,211,238,0.18)"
            >
              <Avatar
                size="lg"
                name=""
                border="2px solid"
                borderColor="rgba(15,10,26,0.35)"
                src={avatarSrc ?? ''}
              />
              <Box
                position="absolute" bottom="2px" right="2px"
                w="11px" h="11px" borderRadius="full"
                bg="green.400" border="2px solid white"
              />
            </Box>

            <VStack align="start" spacing={0.5}>
              <Text fontSize="xs" color="whiteAlpha.700" fontWeight="medium" letterSpacing="0.04em">
                {t(getGreetingKey())},
              </Text>
              <Text fontSize="2xl" fontWeight="800" color="white" letterSpacing="tight" lineHeight="1.1">
                {t('shared.common.dr')} {userData?.contactName}
              </Text>
              <HStack spacing={3} mt={0.5}>
                {userData?.companyName && (
                  <HStack spacing={1.5}>
                    <BuildingIcon boxSize={3} color="whiteAlpha.600" />
                    <Text fontSize="xs" color="whiteAlpha.700" noOfLines={1}>
                      {userData.companyName}
                    </Text>
                  </HStack>
                )}
                {(isPremium || isEnterprise) && (
                  <Badge
                    bg="rgba(255,255,255,0.18)"
                    color="white"
                    borderRadius="full"
                    px={2}
                    py={0.5}
                    fontSize="2xs"
                    fontWeight="semibold"
                    letterSpacing="0.06em"
                  >
                    ✦ {isPremium ? 'Premium' : 'Institution'}
                  </Badge>
                )}
              </HStack>
            </VStack>
          </HStack>

          {/* Right — quick actions + notifications */}
          <HStack spacing={2} alignSelf={{ base: 'flex-end', md: 'center' }} flexWrap="wrap" justify="flex-end">
            {quickActions.map(({ icon, label, path, color }) => (
              <Tooltip key={path} label={label} hasArrow placement="bottom" openDelay={250} bg="gray.900" color="white" borderRadius="md">
                <IconButton
                  aria-label={label}
                  icon={<Icon as={icon} boxSize={5} />}
                  variant="ghost"
                  color="white"
                  borderRadius="xl"
                  size="md"
                  bg="rgba(255,255,255,0.08)"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  sx={{ backdropFilter: 'blur(8px)' }}
                  _hover={{
                    bg: 'rgba(255,255,255,0.16)',
                    borderColor: 'rgba(34,211,238,0.5)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 22px -8px ${color}, 0 0 14px -4px ${color}`,
                    '& svg': { filter: `drop-shadow(0 0 6px ${color})`, transform: 'scale(1.08)' },
                  }}
                  _active={{ transform: 'translateY(0)' }}
                  transition="all 0.22s cubic-bezier(0.4,0,0.2,1)"
                  onClick={() => navigate(path)}
                />
              </Tooltip>
            ))}
            <Box w="1px" h="28px" mx={1} bgGradient="linear(to-b, transparent, rgba(34,211,238,0.55), transparent)" />
            <NotificationsPanel />
          </HStack>
      </Flex>
    </Box>
  )
}
